<template>
  <div class="broker-profile">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-6">Profile Settings</h1>
      </v-col>
    </v-row>

    <v-row>
      <!-- Profile Information Card -->
      <v-col cols="12" md="4">
        <v-card class="profile-card">
          <v-card-text class="text-center">
            <v-avatar size="120" class="mb-4">
              <v-icon size="60">fas fa-user-circle</v-icon>
            </v-avatar>
            <h2 class="text-h5 mb-2">{{ userProfile.firstName }} {{ userProfile.lastName }}</h2>
            <p class="text-subtitle-1 grey--text">{{ userProfile.companyName }}</p>
            <p class="text-caption">{{ userProfile.email }}</p>

            <v-divider class="my-4"></v-divider>

            <div class="text-left">
              <p class="text-caption grey--text mb-1">Role</p>
              <p class="font-weight-medium">{{ userProfile.role === 'broker_admin' ? 'Broker Administrator' : 'Broker' }}</p>

              <p class="text-caption grey--text mb-1 mt-3">Member Since</p>
              <p class="font-weight-medium">{{ formatDate(userProfile.createdAt) }}</p>

              <p class="text-caption grey--text mb-1 mt-3">Last Login</p>
              <p class="font-weight-medium">{{ formatDate(userProfile.lastLogin) }}</p>
            </div>

            <v-btn color="primary" block class="mt-4" @click="showAvatarDialog = true">
              <v-icon left>fas fa-camera</v-icon>
              Change Avatar
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Profile Form -->
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title>
            Personal Information
            <v-spacer></v-spacer>
            <v-btn icon @click="editMode = !editMode">
              <v-icon>{{ editMode ? 'fas fa-times' : 'fas fa-pencil-alt' }}</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-form ref="profileForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="userProfile.firstName"
                    label="First Name"
                    :readonly="!editMode"
                    :rules="nameRules"
                    outlined
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="userProfile.lastName"
                    label="Last Name"
                    :readonly="!editMode"
                    :rules="nameRules"
                    outlined
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="userProfile.email"
                    label="Email Address"
                    :readonly="!editMode"
                    :rules="emailRules"
                    outlined
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="userProfile.companyName"
                    label="Company Name"
                    :readonly="!editMode"
                    outlined
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="userProfile.phone"
                    label="Phone Number"
                    :readonly="!editMode"
                    outlined
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="userProfile.timezone"
                    :items="timezones"
                    label="Timezone"
                    :readonly="!editMode"
                    outlined
                  />
                </v-col>
              </v-row>

              <v-btn
                v-if="editMode"
                color="primary"
                :loading="saving"
                :disabled="!formValid"
                @click="saveProfile"
              >
                Save Changes
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Security Settings -->
        <v-card class="mt-4">
          <v-card-title>Security Settings</v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item @click="showPasswordDialog = true">
                <v-list-item-icon>
                  <v-icon>fas fa-lock</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Change Password</v-list-item-title>
                  <v-list-item-subtitle>Update your account password</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon>fas fa-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>

              <v-list-item>
                <v-list-item-icon>
                  <v-icon>fas fa-shield-alt</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>Two-Factor Authentication</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ twoFactorEnabled ? 'Enabled' : 'Disabled' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-switch
                    v-model="twoFactorEnabled"
                    @change="toggleTwoFactor"
                  />
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Change Password Dialog -->
    <v-dialog v-model="showPasswordDialog" max-width="500">
      <v-card>
        <v-card-title>Change Password</v-card-title>
        <v-card-text>
          <v-form ref="passwordForm" v-model="passwordFormValid">
            <v-text-field
              v-model="passwordData.current"
              label="Current Password"
              type="password"
              :rules="passwordRules"
              outlined
            />
            <v-text-field
              v-model="passwordData.new"
              label="New Password"
              type="password"
              :rules="newPasswordRules"
              outlined
            />
            <v-text-field
              v-model="passwordData.confirm"
              label="Confirm New Password"
              type="password"
              :rules="confirmPasswordRules"
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPasswordDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="changingPassword"
            :disabled="!passwordFormValid"
            @click="changePassword"
          >
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import authService from '../services/auth.service'

export default {
  name: 'BrokerProfile',
  data() {
    return {
      editMode: false,
      formValid: false,
      passwordFormValid: false,
      saving: false,
      changingPassword: false,
      showPasswordDialog: false,
      showAvatarDialog: false,
      twoFactorEnabled: false,
      userProfile: {
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        phone: '',
        timezone: 'UTC',
        role: '',
        createdAt: new Date(),
        lastLogin: new Date()
      },
      passwordData: {
        current: '',
        new: '',
        confirm: ''
      },
      timezones: [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Asia/Hong_Kong',
        'Australia/Sydney'
      ],
      nameRules: [
        v => !!v || 'Name is required',
        v => v.length >= 2 || 'Name must be at least 2 characters'
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        v => !!v || 'Password is required'
      ],
      newPasswordRules: [
        v => !!v || 'New password is required',
        v => v.length >= 6 || 'Password must be at least 6 characters'
      ]
    }
  },
  computed: {
    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.passwordData.new || 'Passwords must match'
      ]
    }
  },
  mounted() {
    this.loadProfile()
  },
  methods: {
    loadProfile() {
      const user = authService.getCurrentUser()
      if (user) {
        this.userProfile = { ...this.userProfile, ...user }
      }
    },
    async saveProfile() {
      if (!this.formValid) return

      this.saving = true
      // Mock save - in production, this would call the API
      setTimeout(() => {
        this.saving = false
        this.editMode = false
        this.$vuetify.goTo(0)
        // Show success message
      }, 1000)
    },
    async changePassword() {
      if (!this.passwordFormValid) return

      this.changingPassword = true
      // Mock password change - in production, this would call the API
      setTimeout(() => {
        this.changingPassword = false
        this.showPasswordDialog = false
        this.passwordData = { current: '', new: '', confirm: '' }
        // Show success message
      }, 1000)
    },
    toggleTwoFactor() {
      // Mock 2FA toggle - in production, this would call the API
      console.log('2FA toggled:', this.twoFactorEnabled)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.broker-profile {
  .profile-card {
    position: sticky;
    top: 80px;
  }
}
</style>